/**
 * LandingDeadlineRow — single row inside the LandingPage hero panel.
 *
 * Pure presentational: receives the pre-shaped row object (already mapped
 * from either live API data or a demo set) and renders the tiered visual:
 * icon/dot, title, urgent pill, optional amount label, workspace chip,
 * and the relative-time string. The "tier" classes (urgent/soon/later)
 * drive the heat-map styling via tokens.css.
 *
 * Click semantics: clickable only when `item.itemType` is set (i.e. live
 * data). Demo rows render as a `<div>` and are inert.
 */
import { ArrowUpRight } from 'lucide-react';

export const LandingDeadlineRow = ({ item, idx, isLast, meta, onRowClick }) => {
  const Icon = meta?.icon;
  const isClickable = Boolean(meta);
  const RowTag = isClickable ? 'button' : 'div';

  return (
    <li
      data-testid={`landing-deadline-${item.id}`}
      style={isLast ? {} : { borderBottom: '1px dashed var(--pd-border)' }}
    >
      <RowTag
        type={isClickable ? 'button' : undefined}
        onClick={isClickable ? () => onRowClick(item) : undefined}
        aria-label={isClickable ? `Open ${meta.label.toLowerCase()}: ${item.title}` : undefined}
        data-testid={isClickable ? `landing-deadline-link-${item.id}` : undefined}
        data-tier={item.tier}
        className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl text-left pd-deadline-row pd-deadline-row--${item.tier} ${isClickable ? 'pd-deadline-row-interactive' : ''}`}
        style={{ background: 'transparent', border: 'none' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {Icon ? (
            <span
              aria-hidden
              className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 pd-deadline-icon pd-deadline-icon--${item.tier}`}
            >
              <Icon className="w-3 h-3" />
            </span>
          ) : (
            <span
              aria-hidden
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 pd-deadline-dot pd-deadline-dot--${item.tier}`}
              style={{ animationDelay: `${idx * 0.45}s` }}
            />
          )}
          <span className="text-[14px] font-medium truncate" style={{ color: 'var(--pd-text)' }}>
            {item.title}
          </span>
          {item.tier === 'urgent' && (
            <span
              data-testid={`landing-urgent-pill-${item.id}`}
              className="pd-urgent-pill text-[9px] uppercase tracking-[0.12em] font-bold rounded-full px-1.5 py-0.5 flex-shrink-0"
            >
              Urgent
            </span>
          )}
          {item.amountLabel && (
            <span
              className="text-[10px] font-mono tabular-nums rounded-full px-1.5 py-0.5 flex-shrink-0"
              style={{ background: 'var(--pd-brand-soft, var(--pd-panel-2))', color: 'var(--pd-text)' }}
            >
              {item.amountLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span
            className="text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5"
            style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-muted)' }}
          >
            {item.workspace}
          </span>
          <span className={`text-[12px] font-mono tabular-nums w-16 text-right pd-deadline-time pd-deadline-time--${item.tier}`}>
            {item.due}
            {/* Live-data caret only on the most-urgent row — quiet "this is updating" cue. */}
            {idx === 0 && <span aria-hidden className="pd-deadline-tick" />}
          </span>
        </div>
      </RowTag>
    </li>
  );
};

// Re-export the arrow icon used by the parent CTA so callers don't have to
// reach into lucide-react themselves — convenience only.
export { ArrowUpRight };

export default LandingDeadlineRow;
